import { MinzeElement, Reactive, EventListeners } from 'minze'
import {
  reauthenticateWithCredential,
  updatePassword,
  UserCredential,
  GoogleAuthProvider,
  FacebookAuthProvider,
  EmailAuthProvider,
  unlink,
  linkWithRedirect,
} from 'firebase/auth'
import type { User } from 'firebase/auth'
import {
  EMAIL_REGEX,
  PASSWORD_REGEX,
  unescapeHtml,
  escapeHtml,
  getVisibleWidth,
} from '../../modules/utils'
import { Routing } from '../../modules/routing'
import { Locale } from '../../modules/locale'
import { Font, SCREEN_WIDTHS } from '../../constants'
import MainModule from '../../modules/main'
import { Callback } from '../../modules/callback'

Locale.getInstance().registerKeySet('fr', 'profileSecurity', {
  errors: {
    oldPassword: 'Veuillez entrer votre mot de passe actuel',
    incorrectOldPassword: 'Le mot de passe actuel est incorrect',
    newPassword: 'Veuillez entrer un mot de passe valide',
    confirmPassword: 'Les mots de passe ne correspondent pas',
    passwordNotRespected:
      'Votre mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial',
    passwordLength: 'Le mot de passe doit contenir au moins 8 caractères',
    misc: 'Une erreur est survenue',
    changeEmail: {
      password: 'Veuillez entrer votre mot de passe',
      newEmail: 'Veuillez entrer un courriel valide',
      confirmNewEmail: 'Les deux courriels ne correspondent pas',
    },
  },
  providers: {
    google: 'Google',
    facebook: 'Facebook',
    email: 'Email',
    notLinked: 'Non lié',
  },
  providerConnection: 'Connexion avec Google, Facebook',
  account: 'Compte',
  legalInformationTitle: 'Informations légales',
  termsAndConditions: 'Termes et conditions',
  privacyPolicy: 'Politique de confidentialité',
  save: 'Sauvegarder',
  cancel: 'Annuler',
  modify: 'Modifier',
  createPassword: 'Créer un mot de passe',
  deleteAccount: 'Supprimer mon compte',
  deleteAccountConfirmation:
    'Êtes-vous sûr de vouloir supprimer votre compte? Cette action est irréversible.',
  deleteAccountConfirmationModalTitle: 'Supprimer mon compte',
  deleteAccountConfirmationModalText:
    'Êtes-vous certains de vouloir supprimer votre compte? Cette action est irréversible.',
  deleteAccountConfirmationModalInputLabel: 'Confirmer mon mot de passe',
  deleteAccountConfirmationModalInputPlaceholder: 'Entrez votre mot de passe',
  deleteAccountConfirmationModalPasswordError: 'Le mot de passe saisi est incorrect',
  deleteAccountConfirmationModalCancelButton: 'Annuler',
  deleteAccountConfirmationModalConfirmButton: 'Supprimer mon compte',
  delete: 'Supprimer',
  deleteAccountTermsAndConditionsMode: {
    title: "RETIRER MON CONSENTEMENT AUX CONDITIONS D'UTILISATION",
    text: "Pour préserver votre compte à Lavitrine.com, vous devez consentir aux Conditions d'utilisation. Si vous retirez votre consentement aux Conditions d'utilisation de Lavitrine.com, vous devrez supprimer votre compte.",
    linkLabel: 'En savoir plus…',
    linkUrl: `${MainModule.getInstance().getRouting().getRoute('termsOfUse')}`,
    tooltipTitle: "Conditions d'utilisation",
    tooltipText:
      "Vous avez consenti aux conditions d'utilisation en créant un compte sur Lavitrine.com. Si vous désirez retirer votre consentement, vous devez supprimer votre compte. Pour procéder, veuillez décocher la case des conditions d'utilisation.",
    tooltipLink: 'En savoir plus',
    tooltipUrl: `${MainModule.getInstance().getRouting().getRoute('termsOfUse')}`,
  },
  deleteAccountPrivacyPolicyMode: {
    title: 'RETIRER MON CONSENTEMENT À LA POLITIQUE DE CONFIDENTIALITÉ',
    text: 'Pour préserver votre compte à Lavitrine.com, vous devez consentir à la Politique de confidentialité. Si vous retirez votre consentement à la Politique de confidentialité de Lavitrine.com, vous devrez supprimer votre compte. Si vous cherchez plutôt à modifier vos préférences de suivi des cookies, cliquez sur le lien “Changer mes préférences” ci-dessous.',
    link1Label: 'En savoir plus…',
    link1Url: `${MainModule.getInstance().getRouting().getRoute('privacyPolicy')}`,
    link2Label: 'Changer mes préférences',
    link2Url: 'Axept.io',
    tooltipTitle: 'Politique de confidentialité',
    tooltipText:
      'Vous avez consenti à la politique de confidentialité en créant un compte sur Lavitrine.com. Si vous désirez retirer votre consentement, vous devez supprimer votre compte. Pour procéder, veuillez décocher la case à droite de la politique de confidentialité.',
    tooltipLink: 'En savoir plus',
    tooltipUrl: `${MainModule.getInstance().getRouting().getRoute('privacyPolicy')}`,
  },
  unlinkProviderErrorModal: {
    title: {
      'google.com': 'Délier son compte Google',
      'facebook.com': 'Délier son compte Facebook',
    },
    text: {
      'google.com':
        "Votre compte a été créé à l'aide de l'authentification unique (SSO), vous devez vous créer un mot de passe avant de pouvoir délier votre compte Google. Voulez-vous créer votre mot de passe maintenant?",
      'facebook.com':
        "Votre compte a été créé à l'aide de l'authentification unique (SSO), vous devez vous créer un mot de passe avant de pouvoir délier votre compte Facebook. Voulez-vous créer votre mot de passe maintenant?",
    },
    buttons: {
      cancel: 'Annuler',
      submit: 'Créer un mot de passe',
    },
  },
  link: 'Lier',
  unlink: 'Délier',
  notLinked: 'Non lié',
  email: 'Courriel',
  password: 'Mot de passe',
  oldPassword: 'Mot de passe actuel',
  newPassword: 'Nouveau mot de passe',
  confirmPassword: 'Confirmer le nouveau mot de passe',
  passwordChanged: 'Votre mot de passe a été changé',
  clickOnModify: 'Cliquez sur le bouton modifier pour changer votre mot de passe',
  changeEmail: {
    password: 'Entrer votre mot de passe',
    newEmail: 'Nouveau courriel',
    confirmNewEmail: 'Confirmer le nouveau courriel',
    emailSentNotification: 'Courriel de confirmation envoyé au nouveau courriel',
  },
  createPasswordNotification: 'Lien de création de mot de passe envoyé à votre courriel',
})

Locale.getInstance().registerKeySet('en', 'profileSecurity', {
  errors: {
    oldPassword: 'Please enter your current password',
    incorrectOldPassword: 'The current password is incorrect',
    newPassword: 'Please enter a valid password',
    confirmPassword: 'Passwords do not match',
    passwordNotRespected:
      'Your password must have at least 8 characters, one uppercase letter, one lowercase letter, one number and one special character',
    passwordLength: 'The password must contain at least 8 characters',
    misc: 'An error occurred',
    changeEmail: {
      password: 'Please enter your password',
      newEmail: 'Please enter a valid email',
      confirmNewEmail: 'The emails do not match',
    },
  },
  providers: {
    google: 'Google',
    facebook: 'Facebook',
    email: 'Email',
    notLinked: 'Not linked',
  },
  providerConnection: 'Connect with Google, Facebook',
  account: 'Account',
  legalInformationTitle: 'Legal information',
  termsAndConditions: 'Terms and conditions',
  privacyPolicy: 'Privacy Policy',
  save: 'Save',
  cancel: 'Cancel',
  modify: 'Change',
  createPassword: 'Create a password',
  deleteAccount: 'Delete my account',
  deleteAccountConfirmation:
    'Are you sure you want to delete your account? This action is irreversible.',
  deleteAccountConfirmationModalTitle: 'Delete my account',
  deleteAccountConfirmationModalText:
    'Are you sure you want to delete your account? This is an  irreversible action.',
  deleteAccountConfirmationModalInputLabel: 'Confirm your password',
  deleteAccountConfirmationModalInputPlaceholder: 'Enter your password',
  deleteAccountConfirmationModalPasswordError: 'The password you entered is incorrect',
  deleteAccountConfirmationModalCancelButton: 'Cancel',
  deleteAccountConfirmationModalConfirmButton: 'Delete my account',
  delete: 'Delete',
  deleteAccountTermsAndConditionsMode: {
    title: 'WITHDRAW MY CONSENT TO THE TERMS AND CONDITIONS',
    text: 'To preserve your account at Lavitrine.com, you must agree to the Terms and conditions. If you withdraw your consent to the Lavitrine.com Terms and conditions, you will need to delete your account.',
    linkLabel: 'I want to know more…',
    linkUrl: `<a href='${MainModule.getInstance().getRouting().getRoute('termsOfUse')}' target='_blank'>`,
    tooltipTitle: 'Terms and conditions',
    tooltipText:
      'You have given your consent to the terms and conditions when you created your account on Lavitrine.com. If you desire to withdraw your consent, you will need to delete your account. To do so, uncheck the toggle to the right of the terms and conditions.',
    tooltipLink: 'Learn more',
    tooltipUrl: `<a href='${MainModule.getInstance().getRouting().getRoute('termsOfUse')}' target='_blank'>`,
  },
  deleteAccountPrivacyPolicyMode: {
    title: 'WITHDRAW MY CONSENT TO THE PRIVACY POLICY',
    text: 'To preserve your account at Lavitrine.com, you must agree to the Privacy policy. If you withdraw your consent to the Lavitrine.com Privacy policy, you will need to delete your account. If you are looking to change your cookies consent settings, click on the “Change my preferences” link below instead.',
    link1Label: 'I want to know more…',
    link1Url: `${MainModule.getInstance().getRouting().getRoute('privacyPolicy')}`,
    link2Label: 'Change my preferences',
    link2Url: 'https://www.axept.io/',
    tooltipTitle: 'Privacy policy',
    tooltipText:
      'You have given your consent to the privacy policy when you created your account on Lavitrine.com. If you desire to withdraw your consent, you will need to delete your account. To do so, disable the toggle to the right of the privacy policy.',
    tooltipLink: 'Learn more',
    tooltipUrl: `<a href='${MainModule.getInstance().getRouting().getRoute('privacyPolicy')}' target='_blank'>`,
  },
  unlinkProviderErrorModal: {
    title: {
      'google.com': 'Unlink your Google account',
      'facebook.com': 'Unlink your Facebook account',
    },
    text: {
      'google.com':
        'Your account was created through SSO, you will need to create login credentials before you can unlink your Google account. Do you want to create your password now?',
      'facebook.com':
        'Your account was created through SSO, you will need to create login credentials before you can unlink your Facebook account. Do you want to create your password now?',
    },
    buttons: {
      cancel: 'Cancel',
      submit: 'Create a password',
    },
  },
  link: 'Link',
  unlink: 'Unlink',
  notLinked: 'Not linked',
  email: 'Email',
  password: 'Password',
  oldPassword: 'Current password',
  newPassword: 'New password',
  confirmPassword: 'Confirm new password',
  passwordChanged: 'Password changed',
  clickOnModify: 'Click on the modify button to change your password',
  changeEmail: {
    password: 'Enter your password',
    newEmail: 'New email',
    confirmNewEmail: 'Confirm new email',
    emailSentNotification: 'Confirmation e-mail sent to new e-mail address',
  },
  createPasswordNotification: 'Password creation link has been sent to your email',
})

const initialErrorsState = {
  oldPassword: false,
  newPassword: false,
  confirmPassword: false,
  changeEmail: {
    password: false,
    newEmail: false,
    confirmNewEmail: false,
  },
}

export interface LvcProfileSecurityModalContent {}

export class LvcProfileSecurityModalContent extends MinzeElement {
  private onChangeCb: string = ''
  private onCreatePasswordBusy: boolean = false
  reactive: Reactive = [
    ['isDesktop', true],
    ['email', ''],
    ['changePasswordDrawer', true],
    ['changeEmailDrawer', true],
    ['providers', []],
    ['unlinkProviderErrorModalOpened', false],
    ['unlinkingProvider', ''],
    ['deleteConfirmationPasswordError', false],
    ['privacyPolicyTooltipOpened', false],
    ['termsAndConditionsTooltipOpened', false],
  ]

  errors: any = initialErrorsState

  updateInfo() {
    const mainModule = MainModule.getInstance()
    const userAuth = mainModule.getAuth().getUserAuth()

    if (userAuth && userAuth.email) {
      this.email = userAuth.email
      let providers: any[] = []
      if (userAuth.providerData) providers = [...userAuth.providerData]
      this.providers = providers
    } else {
      this.email = ''
    }
    this.rerender()
  }

  onChange() {
    // Get values of all the inputs
    const inputIds = [
      'oldPassword',
      'newPassword',
      'confirmPassword',
      'newEmailPassword',
      'newEmail',
      'confirmNewEmail',
    ]
    const values = inputIds.reduce(
      (acc, id) => {
        const element = this.select(`[name=\"${id}\"]`) as HTMLInputElement
        acc[id] = element?.value || ''
        return acc
      },
      {} as { [key: string]: string }
    )

    // Check if there are any non-empty values
    const hasValues = Object.values(values).some(value => value.trim() !== '')

    // Set the page to dirty or clearDirty accordingly
    const mainModule = MainModule.getInstance()
    const routing = mainModule.getRouting()
    if (hasValues) {
      routing.setDirty()
    } else {
      routing.clearDirty()
    }
  }

  onReady(): void | Promise<void> {
    this.isDesktop = getVisibleWidth() > SCREEN_WIDTHS.DESTKOP_LARGE
    this.updateInfo()
    this.onChangeCb = Callback.attach(this.onChange.bind(this))
  }

  handleAuthChanged = (event: Event) => {
    this.updateInfo()
  }

  onDrawerToggle = (event: Event) => {
    this.changePasswordDrawer = !this.changePasswordDrawer
    this.rerender()
    const oldPasswordElement = this.select('[name="oldPassword"]') as HTMLInputElement
    const newPasswordElement = this.select('[name="newPassword"]') as HTMLInputElement
    const confirmPasswordElement = this.select('[name="confirmPassword"]') as HTMLInputElement
    oldPasswordElement.value = ''
    newPasswordElement.value = ''
    confirmPasswordElement.value = ''
    this.errors = {
      ...this.errors,
      oldPassword: false,
      newPassword: false,
      confirmPassword: false,
    }
  }

  onEmailDrawerToggle = (event: Event) => {
    this.changeEmailDrawer = !this.changeEmailDrawer
    this.rerender()
    const passwordElement = this.select('[name="newEmailPassword"]') as HTMLInputElement
    const newPasswordElement = this.select('[name="newPassword"]') as HTMLInputElement
    const confirmPasswordElement = this.select('[name="confirmPassword"]') as HTMLInputElement
    passwordElement.value = ''
    newPasswordElement.value = ''
    confirmPasswordElement.value = ''
    this.errors.changeEmail = initialErrorsState.changeEmail
  }

  onInputChange = (event: Event) => {
    for (const key in this.errors) {
      this.errors[key] = false
    }
    this.rerender()
  }

  changeEmail = async () => {
    const mainModule = MainModule.getInstance()
    const auth = mainModule.getAuth()
    const userAuth = mainModule.getAuth().getUserAuth()
    const email = userAuth.email
    const passwordElement = this.select('[name="newEmailPassword"]') as HTMLInputElement
    const newEmailElement = this.select('[name="newEmail"]') as HTMLInputElement
    const confirmNewEmailElement = this.select('[name="confirmNewEmail"]') as HTMLInputElement
    const password = unescapeHtml(passwordElement.value)
    const newEmail = unescapeHtml(newEmailElement.value)
    const confirmNewEmail = unescapeHtml(confirmNewEmailElement.value)
    for (const key in this.errors.changeEmail) {
      this.errors.changeEmail[key] = false
    }

    if (newEmail !== confirmNewEmail) {
      this.errors.changeEmail.confirmNewEmail = Locale.getKey(
        'profileSecurity.errors.changeEmail.confirmNewEmail'
      )
    }
    if (!newEmail) {
      this.errors.changeEmail.newEmail = Locale.getKey(
        'profileSecurity.errors.changeEmail.newEmail'
      )
    }

    if (newEmail && !EMAIL_REGEX.test(newEmail)) {
      this.errors.changeEmail.newEmail = Locale.getKey(
        'profileSecurity.errors.changeEmail.newEmail'
      )
    }

    if (!password) {
      this.errors.changeEmail.password = Locale.getKey(
        'profileSecurity.errors.changeEmail.password'
      )
    }

    let hasError = false
    for (const key in this.errors.changeEmail) {
      if (this.errors.changeEmail[key]) {
        hasError = true
      }
    }

    if (hasError) {
      this.rerender()
      return
    }

    let session

    try {
      session = await reauthenticateWithCredential(
        auth.getUserAuth(),
        EmailAuthProvider.credential(email, password)
      )
    } catch (error) {
      this.errors.changeEmail.password = Locale.getKey(
        'profileSecurity.errors.incorrectOldPassword'
      )
      this.rerender()
      return
    }

    if (!session) {
      this.errors.changeEmail.password = Locale.getKey(
        'profileSecurity.errors.incorrectOldPassword'
      )
      this.rerender()
      return
    }

    try {
      const mainModule = MainModule.getInstance()
      try {
        await mainModule.getAuth().changeEmail(newEmail)
        mainModule.showToastAlert(
          Locale.getKey('profileSecurity.changeEmail.emailSentNotification')
        )
      } catch (e) {}
      this.changeEmailDrawer = true
      passwordElement.value = ''
      newEmailElement.value = ''
      confirmNewEmailElement.value = ''
      MainModule.getInstance().getRouting().clearDirty()
      this.rerender()
    } catch (error) {
      this.errors.changeEmail.newPassword = Locale.getKey('profileSecurity.errors.misc')
      this.rerender()
    }
  }

  save = async () => {
    const mainModule = MainModule.getInstance()
    const auth = mainModule.getAuth()
    const userAuth = mainModule.getAuth().getUserAuth()
    const email = userAuth.email
    const oldPasswordElement = this.select('[name="oldPassword"]') as HTMLInputElement
    const newPasswordElement = this.select('[name="newPassword"]') as HTMLInputElement
    const confirmPasswordElement = this.select('[name="confirmPassword"]') as HTMLInputElement
    const oldPassword = unescapeHtml(oldPasswordElement.value)
    const newPassword = unescapeHtml(newPasswordElement.value)
    const confirmPassword = unescapeHtml(confirmPasswordElement.value)
    for (const key in this.errors) {
      this.errors[key] = false
    }

    if (newPassword !== confirmPassword) {
      this.errors.confirmPassword = Locale.getKey('profileSecurity.errors.confirmPassword')
    }
    if (!newPassword) {
      this.errors.newPassword = Locale.getKey('profileSecurity.errors.newPassword')
    }

    if (newPassword && newPassword.length < 8) {
      this.errors.newPassword = Locale.getKey('profileSecurity.errors.passwordLength')
    }

    if (newPassword && !PASSWORD_REGEX.test(newPassword)) {
      this.errors.newPassword = Locale.getKey('profileSecurity.errors.passwordNotRespected')
    }

    if (!oldPassword) {
      this.errors.oldPassword = Locale.getKey('profileSecurity.errors.oldPassword')
    }

    let hasError = false
    for (const key in this.errors) {
      if (this.errors[key]) {
        hasError = true
      }
    }

    if (hasError) {
      this.rerender()
      return
    }

    let session

    try {
      session = await reauthenticateWithCredential(
        auth.getUserAuth(),
        EmailAuthProvider.credential(email, oldPassword)
      )
    } catch (error) {
      this.errors.oldPassword = Locale.getKey('profileSecurity.errors.incorrectOldPassword')
      this.rerender()
      return
    }

    if (!session) {
      this.errors.oldPassword = Locale.getKey('profileSecurity.errors.incorrectOldPassword')
      this.rerender()
      return
    }

    try {
      await updatePassword(auth.getUserAuth(), newPassword)

      session = await reauthenticateWithCredential(
        auth.getUserAuth(),
        EmailAuthProvider.credential(email, newPassword)
      )
      if (!session) {
        throw new Error('User not found')
      }
      this.changePasswordDrawer = true
      oldPasswordElement.value = ''
      newPasswordElement.value = ''
      confirmPasswordElement.value = ''
      let mainModule = MainModule.getInstance()
      mainModule.getRouting().clearDirty()
      mainModule.showToastAlert(Locale.getKey('profileSecurity.passwordChanged'))
      this.rerender()
    } catch (error) {
      this.errors.newPassword = Locale.getKey('profileSecurity.errors.misc')
      this.rerender()
    }
    mainModule.getRouting().clearDirty()
  }

  onRemoveProvider = async (event: Event) => {
    const mainModule = MainModule.getInstance()
    const auth = mainModule.getAuth()
    const providerId: string = (event.target as HTMLElement).getAttribute('provider-id') as string

    if (!(this.providers as Array<any>)?.find(provider => provider.providerId === 'password')) {
      this.unlinkingProvider = providerId
      return (this.unlinkProviderErrorModalOpened = true)
    }

    const fbAuth = auth.getFirebaseAuth()

    try {
      await unlink(fbAuth.currentUser, providerId)
      this.updateInfo()
      this.rerender()
    } catch (error) {
      console.error(error)
    }
  }

  onLinkProvider = async (event: Event) => {
    const mainModule = MainModule.getInstance()
    const auth = mainModule.getAuth()
    const fbAuth = auth.getFirebaseAuth()
    const providerId: string = (event.target as HTMLElement).getAttribute('provider-id') as string
    let provider: any
    if (providerId === 'google.com') {
      provider = new GoogleAuthProvider()
    } else if (providerId === 'facebook.com') {
      provider = new FacebookAuthProvider()
    }
    try {
      await linkWithRedirect(fbAuth.currentUser, provider)
      this.rerender()
    } catch (error) {
      console.error(error)
    }
  }

  openDeleteAccountModal = (privacyPolicy?: boolean, termsAndConditions?: boolean) => {
    const passwordProvider = (this.providers as Array<any>)?.find(
      provider => provider.providerId === 'password'
    )
    let attr: { name: string; value: string }[] = []
    if (privacyPolicy) {
      attr.push({
        name: 'delete-confirmation-privacy-policy-mode',
        value: 'true',
      })
    }
    if (termsAndConditions) {
      attr.push({
        name: 'delete-confirmation-terms-and-conditions-mode',
        value: 'true',
      })
    }
    if (passwordProvider) {
      attr.push({ name: 'password-provider', value: 'true' })
    }
    MainModule.getInstance().injectIntoDOM('lvc-delete-profile-popup', attr)
  }

  closeUnlinkProviderModal = () => {
    this.unlinkProviderErrorModalOpened = false
  }

  submitCreatePasswordRequest = async () => {
    if (this.onCreatePasswordBusy) return

    this.onCreatePasswordBusy = true
    this.rerender()
    const mainModule = MainModule.getInstance()
    try {
      await mainModule.getAuth().createPassword()
    } catch (e) {}
    mainModule.showToastAlert(Locale.getKey('profileSecurity.createPasswordNotification'))
    this.onCreatePasswordBusy = false
    this.unlinkProviderErrorModalOpened = false
    this.rerender()
  }

  deleteAccount = async (event: Event) => {
    const mainModule = MainModule.getInstance()
    const auth = mainModule.getAuth()
    const detail = (event as CustomEvent).detail
    try {
      this.deleteConfirmationPasswordError = false
      const email = this.email as string
      const passwordProvider = (this.providers as Array<any>)?.find(
        provider => provider.providerId === 'password'
      )
      if (passwordProvider) {
        const password = detail.password as string
        await reauthenticateWithCredential(
          auth.getUserAuth(),
          EmailAuthProvider.credential(email, password)
        )
      }
      let response: any = await auth.deleteAccount(detail.reason || 'none')
      if (response?.success) mainModule.getRouting().goTo(`${Routing.HOST}?account-deleted=true`)
    } catch (e) {
      this.deleteConfirmationPasswordError = Locale.getKey(
        'profileSecurity.deleteAccountConfirmationModalPasswordError'
      )
    }
  }

  closePrivacyPolicyTooltip = () => (this.privacyPolicyTooltipOpened = false)
  openPrivacyPolicyTooltip = () => (this.privacyPolicyTooltipOpened = true)

  closeTermsAndConditionsTooltip = () => (this.termsAndConditionsTooltipOpened = false)
  openTermsAndConditionsTooltip = () => (this.termsAndConditionsTooltipOpened = true)

  capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  displayConsentToggle = (title: string, name: string) => {
    return `
      <lvc-box mt="16px" display="flex" alignItems="center" justifyContent="space-between">
        <lvc-box>
          <lvc-box display="flex" alignItems="center">
            <lvc-text font-preset="medium" font-weight="${Font.weight.bold}">
              ${title}
            </lvc-text>
            ${this[`${name}TooltipOpened`] ? `<div class="close-overlay" data-name="${name + 'TooltipOverlay'}"></div>` : ''}
            <lvc-box ml="4px" pt="2px" position="relative">
              <lvc-help-icon name="${name + 'TooltipToggle'}"></lvc-help-icon>
              <dialog class="tooltip" ${this[`${name}TooltipOpened`] ? 'open' : ''}>
                <h3>${Locale.getKey(`profileSecurity.deleteAccount${this.capitalizeFirstLetter(name)}Mode.tooltipTitle`)}</h3>
                <p>${Locale.getKey(`profileSecurity.deleteAccount${this.capitalizeFirstLetter(name)}Mode.tooltipText`)}</p>
                <a href="${Locale.getKey(`profileSecurity.deleteAccount${this.capitalizeFirstLetter(name)}Mode.tooltipUrl`)}" target="_blank">
                  ${Locale.getKey(`profileSecurity.deleteAccount${this.capitalizeFirstLetter(name)}Mode.tooltipLink`)}
                </a>
              </dialog>
            </lvc-box>
          </lvc-box>
        </lvc-box>
        <lvc-toggle read-only="true" data-action="${'deleteAccount' + this.capitalizeFirstLetter(name) + 'Mode'}" initial-toggled="true"></lvc-toggle>
      </lvc-box>
    `
  }

  html = () => {
    let providers = this.providers as Array<any>
    let googleProviderInstance = new GoogleAuthProvider()
    let facebookProviderInstance = new FacebookAuthProvider()
    let passwordProvider = providers.find((provider: any) => provider.providerId === 'password')
    let facebookProvider = providers.find((provider: any) => provider.providerId === 'facebook.com')
    let googleProvider = providers.find((provider: any) => provider.providerId === 'google.com')
    return `
    <div class="form">
      <lvc-box hide="${!this.changeEmailDrawer}">
        <lvc-box mt="16px" align-items="flex-end" justify-content="flex-end" display="flex">
          <span class="label">${Locale.getKey('profileSecurity.email')}</span>
          <span class="drawer-toggle" name="emailDrawerToggle" >${Locale.getKey('profileSecurity.modify')}</span>
        </lvc-box>
      </lvc-box>
      <lvc-box hide="${!this.changeEmailDrawer}">
        <lvc-text font-preset="small">
          ${this.email}
        </lvc-text>
      </lvc-box>
      <lvc-box hide="${this.changeEmailDrawer}">
        <lvc-box mt="16px" display="flex" align-items="space-between" justify-content="stretch">
          <span class="label">${Locale.getKey('profileSecurity.email')}</span>
          <span class="drawer-toggle" name="emailDrawerToggle" >${Locale.getKey('profileSecurity.cancel')}</span>
        </lvc-box>
        <lvc-box borderBottom="1px solid #1E007D" flexGrow="1" mt="12px"></lvc-box>
        <lvc-box mt="16px">
          <lvc-input name="newEmailPassword" type="password" placeholder="${Locale.getKey('profileSecurity.changeEmail.password')}"
          error="${this.errors.changeEmail.password}" default-value="" label="${Locale.getKey('profileSecurity.changeEmail.password')}" on-change="${this.onChangeCb}"></lvc-input>
        </lvc-box>

        <lvc-box mt="16px">
          <lvc-input name="newEmail" type="email" placeholder="${Locale.getKey('profileSecurity.changeEmail.newEmail')}"
          error="${this.errors.changeEmail.newEmail}" default-value="" label="${Locale.getKey('profileSecurity.changeEmail.newEmail')}" on-change="${this.onChangeCb}"></lvc-input>
        </lvc-box>

        <lvc-box mt="16px">
          <lvc-input name="confirmNewEmail" type="email" placeholder="${Locale.getKey('profileSecurity.changeEmail.confirmNewEmail')}"
          error="${this.errors.changeEmail.confirmNewEmail}" default-value="" label="${Locale.getKey('profileSecurity.changeEmail.confirmNewEmail')}" on-change="${this.onChangeCb}"></lvc-input>

        </lvc-box>
        <lvc-box width="100%" mt="32px">
          <lvc-button enable-hover-box-shadow="true" width="100%" name="updateEmailSubmit">
            ${Locale.getKey('profileSecurity.save')}
          </lvc-button>
        </lvc-box>
      </lvc-box>
      <lvc-box hide="${!this.changePasswordDrawer}">
        <lvc-box mt="16px" align-items="flex-end" justify-content="flex-end" display="flex">
          <span class="label">${Locale.getKey('profileSecurity.password')}</span>
          ${passwordProvider ? `<span class="drawer-toggle" name="drawerToggle" >${Locale.getKey('profileSecurity.modify')}</span>` : ''}
          ${!passwordProvider ? `<span id="create-password-button" class="drawer-toggle ${this.onCreatePasswordBusy ? 'busy' : ''}" name="createPasswordButton" >${Locale.getKey('profileSecurity.createPassword')}</span>` : ''}
        </lvc-box>
      </lvc-box>
      <lvc-box hide="${this.changePasswordDrawer}">
        <lvc-box mt="16px" display="flex" align-items="space-between" justify-content="stretch">
          <span class="label">${Locale.getKey('profileSecurity.password')}</span>
          <span class="drawer-toggle" name="drawerToggle" >${Locale.getKey('profileSecurity.cancel')}</span>
        </lvc-box>
        <lvc-box borderBottom="1px solid #1E007D" flexGrow="1" mt="12px"></lvc-box>
        <lvc-box mt="16px">
          <lvc-input name="oldPassword" type="password" placeholder="${Locale.getKey('profileSecurity.oldPassword')}"
          error="${this.errors.oldPassword}" default-value="" label="${Locale.getKey('profileSecurity.oldPassword')}" on-change="${this.onChangeCb}"></lvc-input>
        </lvc-box>

        <lvc-box mt="16px">
          <lvc-input name="newPassword" type="password" placeholder="${Locale.getKey('profileSecurity.newPassword')}"
          error="${this.errors.newPassword}" default-value="" label="${Locale.getKey('profileSecurity.newPassword')}" on-change="${this.onChangeCb}"></lvc-input>
        </lvc-box>

        <lvc-box mt="16px">
          <lvc-input name="confirmPassword" type="password" placeholder="${Locale.getKey('profileSecurity.confirmPassword')}"
          error="${this.errors.confirmPassword}" default-value="" label="${Locale.getKey('profileSecurity.confirmPassword')}" on-change="${this.onChangeCb}"></lvc-input>

        </lvc-box>
        <lvc-box width="100%" mt="32px">
          <lvc-button enable-hover-box-shadow="true" width="100%" name="save">
            ${Locale.getKey('profileSecurity.save')}
          </lvc-button>
        </lvc-box>
      </lvc-box>
      <lvc-box
        borderBottom="1px solid #1E007D"
        flexGrow="1"
        mt="${window.matchMedia('(max-width: 730px)').matches ? '16px' : '12px'}"
      ></lvc-box>
      <lvc-box mt="16px">
        <lvc-text font-preset="largeExtraBold">
          ${Locale.getKey('profileSecurity.providerConnection').toUpperCase()}
        </lvc-text>
      </lvc-box>
      <lvc-box mt="16px">
        <lvc-box display="flex" align-items="center">
          <lvc-google-icon class="logo"></lvc-google-icon>
          <lvc-box ml="8px">
            <lvc-text font-preset="small" font-weight="${Font.weight.bold}">
              ${Locale.getKey('profileSecurity.providers.google')}
            </lvc-text>
            <lvc-text font-preset="small">
              ${googleProvider?.email ?? Locale.getKey('profileSecurity.providers.notLinked')}
            </lvc-text>
          </lvc-box>
          <lvc-box flexGrow="1"></lvc-box>
            ${
              googleProvider
                ? `
            <lvc-box class="remove" provider-id="${googleProviderInstance?.providerId}">
              ${Locale.getKey('profileSecurity.unlink')}
            </lvc-box>
            `
                : `
            <lvc-box class="link" provider-id="${googleProviderInstance?.providerId}">
              ${Locale.getKey('profileSecurity.link')}
            </lvc-box>
            `
            }
          </lvc-box>
        </lvc-box>
        <lvc-box mt="16px" display="flex" align-items="center">
          <lvc-facebook-icon class="logo"></lvc-facebook-icon>
          <lvc-box ml="8px">
            <lvc-text font-preset="small" font-weight="${Font.weight.bold}">
              ${Locale.getKey('profileSecurity.providers.facebook')}
            </lvc-text>
            <lvc-text font-preset="small">
              ${facebookProvider?.email ?? Locale.getKey('profileSecurity.providers.notLinked')}
            </lvc-text>
          </lvc-box>
          <lvc-box flexGrow="1"></lvc-box>
            ${
              facebookProvider
                ? `
            <lvc-box class="remove" provider-id="${facebookProviderInstance?.providerId}">
              ${Locale.getKey('profileSecurity.unlink')}
            </lvc-box>
            `
                : `
            <lvc-box class="link" provider-id="${facebookProviderInstance?.providerId}">
              ${Locale.getKey('profileSecurity.link')}
            </lvc-box>
            `
            }

        </lvc-box>
      </lvc-box>
      <lvc-box
        borderBottom="1px solid #1E007D"
        flexGrow="1"
        mt="${window.matchMedia('(max-width: 730px)').matches ? '16px' : '12px'}"
      ></lvc-box>
      <lvc-box mt="16px">
        <lvc-text font-preset="largeExtraBold">
          ${Locale.getKey('profileSecurity.account').toUpperCase()}
        </lvc-text>
      </lvc-box>
      <lvc-box mt="16px" display="flex" align-items="center">
        <lvc-box>
          <lvc-text font-preset="small" font-weight="${Font.weight.bold}">
          ${Locale.getKey('profileSecurity.deleteAccount')}
          </lvc-text>
        </lvc-box>
        <lvc-box flexGrow="1"></lvc-box>
          <lvc-box class="delete" name="deleteAccount">
            ${Locale.getKey('profileSecurity.delete')}
          </lvc-box>
        </lvc-box>
      </lvc-box>
      <lvc-box
        borderBottom="1px solid #1E007D"
        flexGrow="1"
        mt="${window.matchMedia('(max-width: 730px)').matches ? '16px' : '12px'}"
      ></lvc-box>
      <lvc-box mt="16px">
        <lvc-text font-preset="largeExtraBold">
          ${Locale.getKey('profileSecurity.legalInformationTitle').toUpperCase()}
        </lvc-text>
      </lvc-box>
      ${this.displayConsentToggle(Locale.getKey('profileSecurity.termsAndConditions'), 'termsAndConditions')}
      ${this.displayConsentToggle(Locale.getKey('profileSecurity.privacyPolicy'), 'privacyPolicy')}
      ${
        this.unlinkProviderErrorModalOpened
          ? `
        <lvc-closeable-modal max-width="512px" no-close-button="true">
          <h3 class="delete-account-title">
            ${Locale.getKey(`profileSecurity.unlinkProviderErrorModal.title.${this.unlinkingProvider}`)}
          </h3>
          <p class="delete-account-text">
            ${Locale.getKey(`profileSecurity.unlinkProviderErrorModal.text.${this.unlinkingProvider}`)}
          </p>
          <div class="delete-account-buttons">
            <lvc-button
              border="1px solid #1E007D"
              padding="14px 24px"
              theme="transparent"
              width="100%"
              class="button"
              id="unlink-provider-cancel-button"
              type="submit"
            >
              ${Locale.getKey(`profileSecurity.unlinkProviderErrorModal.buttons.cancel`)}
            </lvc-button>
            <lvc-button
              padding="14px 24px"
              width="100%"
              class="button"
              id="unlink-provider-confirm-button"
              type="submit"
            >
              ${Locale.getKey(`profileSecurity.unlinkProviderErrorModal.buttons.submit`)}
            </lvc-button>
          </div>
        </lvc-closeable-modal>
      `
          : ``
      }
    </div>
  `
  }

  css = () => `
    :host {
      font-family: ${Font.family};
    }

    .logo {
      width: 32px;
      height: 32px;
      position: relative;
    }

    .logo > svg {
      width: 100%;
      height: 100%;
    }

    .remove, .link, .drawer-toggle, .delete {
      font-family: ${Font.family};
      cursor: pointer;
      font-size: 14px;
      font-weight: ${Font.weight.medium};
      color: #1E007D;
      cursor: pointer;
      text-decoration: underline;
    }

    .remove, .link, .delete {
      display: flex;
      align-items: center;
    }

    .label {
      flex: 1;
      font-size: 14px;
      font-weight: ${Font.weight.bold};
      color: #1E007D;
    }

    .drawer-toggle {
      flex: 1;
      text-align: right;
    }

    .drawer-toggle.busy {
      opacity: 0.5;
    }

    .delete-account-title {
      font-weight: ${Font.weight.extraBold};
      font-size: 24px;
      line-height: 120%;
      color: #1E007D;
      text-transform: uppercase;
      margin: 0;
    }

    .delete-account-text {
      font-weight: ${Font.weight.medium};
      font-size: 17px;
      line-height: 24px;
      color: #1E007D;
      margin: 40px 0;
    }

    .delete-account-buttons {
      margin-top: 40px;
      display: flex;
      gap: 16px;
      justify-content: flex-end;
    }

    .deleteAccountLink {
      color: #8e88d5;
      font-size: 17px;
    }

    lvc-help-icon {
      cursor: pointer;
    }

    .tooltip {
      z-index: 1;
      border: 1px solid rgba(30, 0, 125, 0.3);
      box-shadow: 0px 4px 16px rgba(15, 0, 62, 0.15);
      border-radius: 8px;
      padding: 16px;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 150%;
      color: #1E007D;
      max-width: 90vw;
      width: 340px;
      top: -5px;
      transform: translateY(-100%);
    }

    .tooltip h3 {
      margin-top: 0;
    }

    .close-overlay {
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1;
    }

    @media screen and (max-width: 730px) {
      .delete-account-buttons {
        flex-direction: column;
      }
    }
  `

  eventListeners: EventListeners = [
    [window, 'lvc:user_state_changed', this.handleAuthChanged.bind(this)],
    ['[name="save"]', 'click', this.save.bind(this)],
    ['[name="updateEmailSubmit"]', 'click', this.changeEmail.bind(this)],
    ['[name="drawerToggle"]', 'click', this.onDrawerToggle.bind(this)],
    ['[name="emailDrawerToggle"]', 'click', this.onEmailDrawerToggle.bind(this)],
    ['input', 'change', this.onInputChange.bind(this)],
    ['.remove', 'click', this.onRemoveProvider.bind(this)],
    ['.link', 'click', this.onLinkProvider.bind(this)],
    ['[name="deleteAccount"]', 'click', () => this.openDeleteAccountModal()],
    [
      '[data-action="deleteAccountPrivacyPolicyMode"]',
      'click',
      event => {
        event.preventDefault()
        this.openDeleteAccountModal(true)
      },
    ],
    [
      '[data-action="deleteAccountTermsAndConditionsMode"]',
      'click',
      event => {
        event.preventDefault()
        this.openDeleteAccountModal(false, true)
      },
    ],
    [window, 'lvc:delete_profile_confirmed', this.deleteAccount.bind(this)],
    ['lvc-help-icon[name="privacyPolicyTooltipToggle"]', 'click', this.openPrivacyPolicyTooltip],
    [
      '.close-overlay[data-name="privacyPolicyTooltipOverlay"]',
      'click',
      this.closePrivacyPolicyTooltip,
    ],
    [
      'lvc-help-icon[name="termsAndConditionsTooltipToggle"]',
      'click',
      this.openTermsAndConditionsTooltip,
    ],
    [
      '.close-overlay[data-name="termsAndConditionsTooltipOverlay"]',
      'click',
      this.closeTermsAndConditionsTooltip,
    ],
    ['#unlink-provider-cancel-button', 'click', this.closeUnlinkProviderModal.bind(this)],
    ['#unlink-provider-confirm-button', 'click', this.submitCreatePasswordRequest.bind(this)],
    ['#create-password-button', 'click', this.submitCreatePasswordRequest.bind(this)],
  ]
}
